import store from "../store/index";
import ApiHelpers from "../helpers/ApiHelpers";
import {
  FORUM_BOARD_API,
  FORUM_BOARDS_API,
  FORUM_THREAD_API,
  FORUM_THREAD_PIN_API,
  FORUM_BOARD_ALLOW_STUDENTS,
  FORUM_BOARD_STATUS_API,
  FORUM_MENTIONS_API,
} from "../constants/urls";

export const forumService = {
  // Boards
  fetchBoard,
  fetchVisibleBoards,
  createBoard,
  setBoardStatus,
  setBoardAllowStudentThreads,

  // Threads
  fetchFeed,
  fetchThreadById,
  addThread,
  updateThread,
  deleteThread,
  setThreadPinned,

  // Mentions
  searchMentions,
};

// ---------- Boards ----------

async function fetchBoard(scope, entityId = null) {
  const url = entityId
    ? `${apiBase()}${FORUM_BOARD_API}/${scope}/${entityId}`
    : `${apiBase()}${FORUM_BOARD_API}/${scope}`;
  const response = await ApiHelpers.makeAPICall(url, "GET", null, false);
  return await handleResponse(response);
}

async function fetchVisibleBoards() {
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_BOARDS_API}`,
    "GET",
    null,
    false
  );
  return await handleResponse(response);
}

async function setBoardAllowStudentThreads({ boardScope, entityId, isAllowed }) {
  const body = JSON.stringify({
    BoardScope: boardScope,
    EntityID: entityId,
    IsAllowed: isAllowed,
  });
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_BOARD_ALLOW_STUDENTS}`,
    "PUT",
    body,
    true
  );
  return await handleResponse(response);
}

async function createBoard({ boardScope, entityId }) {
  const body = JSON.stringify({
    BoardScope: boardScope,
    EntityID: entityId,
  });
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_BOARD_API}`,
    "POST",
    body,
    true
  );
  return await handleResponse(response);
}

async function setBoardStatus({ boardScope, entityId, status }) {
  const body = JSON.stringify({
    BoardScope: boardScope,
    EntityID: entityId,
    Status: status,
  });
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_BOARD_STATUS_API}`,
    "PUT",
    body,
    true
  );
  return await handleResponse(response);
}

// ---------- Threads ----------

async function fetchFeed({
  scope,
  entityId = null,
  nextPageToken = null,
  pageSize = 10,
  searchQuery = null,
  sortBy = null,
} = {}) {
  const params = new URLSearchParams();
  if (entityId != null) params.append("EntityID", entityId);
  if (pageSize) params.append("PageSize", pageSize);
  if (nextPageToken) params.append("NextPageToken", nextPageToken);
  if (searchQuery) params.append("SearchQuery", searchQuery);
  if (sortBy) params.append("SortBy", sortBy);

  const url = `${apiBase()}${FORUM_BOARD_API}/${scope}/threads?${params.toString()}`;
  const response = await ApiHelpers.makeAPICall(url, "GET", null, false);
  return await handleEnvelopedListResponse(response);
}

async function fetchThreadById(id) {
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_THREAD_API}/${id}`,
    "GET",
    null,
    false
  );
  return await handleResponse(response);
}

async function addThread({ boardScope, entityId, title, body, attachmentIds = [] }) {
  const requestBody = JSON.stringify({
    BoardScope: boardScope,
    EntityID: entityId,
    Title: title,
    Body: body,
    AttachmentIDs: attachmentIds,
  });
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_THREAD_API}`,
    "POST",
    requestBody,
    true
  );
  return await handleResponse(response);
}

async function updateThread({ id, title, body, attachmentIds = [] }) {
  const requestBody = JSON.stringify({
    ID: id,
    Title: title,
    Body: body,
    AttachmentIDs: attachmentIds,
  });
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_THREAD_API}/${id}`,
    "PUT",
    requestBody,
    true
  );
  return await handleResponse(response);
}

// Mention typeahead. Backend returns UserInfoModel[] with optional Role.
async function searchMentions({ productVariantId = null, searchQuery = "", limit = 10 } = {}) {
  const params = new URLSearchParams();
  if (productVariantId != null) params.append("productVariantId", productVariantId);
  if (searchQuery) params.append("searchQuery", searchQuery);
  if (limit) params.append("limit", limit);

  const url = `${apiBase()}${FORUM_MENTIONS_API}?${params.toString()}`;
  const response = await ApiHelpers.makeAPICall(url, "GET", null, false);
  return await handleResponse(response);
}

async function deleteThread(id) {
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_THREAD_API}/${id}`,
    "DELETE",
    null,
    false
  );
  return await handleResponse(response);
}

async function setThreadPinned({ id, pinned }) {
  const response = await ApiHelpers.makeAPICall(
    `${apiBase()}${FORUM_THREAD_PIN_API}/${id}/pin`,
    pinned ? "POST" : "DELETE",
    null,
    true
  );
  return await handleResponse(response);
}

// ---------- Helpers ----------

function apiBase() {
  return store.state.appState.apiURL;
}

async function handleResponse(response) {
  try {
    const text = await response.text();
    const body = text ? JSON.parse(text) : null;

    if (!response.ok) {
      return Promise.reject(body?.error || { message: "Request failed." });
    }

    if (body && typeof body === "object" && "status" in body && body.status?.toLowerCase() === "success") {
      return body.payload;
    }
    if (body && typeof body === "object" && "payload" in body) {
      return body.payload;
    }
    return body;
  } catch (parseError) {
    return Promise.reject({ message: "Failed to parse response." });
  }
}

async function handleEnvelopedListResponse(response) {
  try {
    const text = await response.text();
    const body = text ? JSON.parse(text) : null;

    if (!response.ok) {
      return Promise.reject(body?.error || { message: "Request failed." });
    }

    return {
      payload: body?.payload || [],
      nextPageToken: body?.nextPageToken || null,
    };
  } catch (parseError) {
    return Promise.reject({ message: "Failed to parse response." });
  }
}
